// Phone number helpers for Indian mobile validation.
//
// Product decision: the reception + booking flows only accept
// **10-digit Indian mobile numbers** starting with 6/7/8/9. This
// mirrors DoT / TRAI's mobile allocation ranges as of 2026 and rejects
// landlines, short codes, and international numbers — anything the
// clinic can't SMS or WhatsApp to for reminders / prescriptions.
//
// The helpers are intentionally lenient about *what the user types*
// (they can paste "+91 98765 43210", "9876-543210", etc.) but strict
// about what's stored — always the bare 10-digit string.
//
// Live use lives in `ReceptionPage.jsx` (patient check-in) and
// `PublicBookingSection.jsx` (public appointment booking).

const IND_MOBILE_RE = /^[6-9]\d{9}$/;

/**
 * Extract just the digits from any user input, then strip a leading
 * `91` if the result is 12 digits (i.e. user typed the country code).
 * Returns a raw digit string — NOT validated. Callers should feed the
 * result into `isValidIndianMobile` to gate submission.
 */
export function normalizeIndianMobile(raw) {
  const digits = String(raw ?? '').replace(/\D+/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

/**
 * True iff the normalised number is exactly 10 digits and starts with
 * one of 6/7/8/9. Empty strings are considered invalid — use
 * `isPartialIndianMobile` for the "still typing" case.
 */
export function isValidIndianMobile(raw) {
  const n = normalizeIndianMobile(raw);
  return IND_MOBILE_RE.test(n);
}

/**
 * True while the user is still typing a plausibly-valid number
 * (0-10 digits, and the first digit — if present — is 6/7/8/9). Used
 * to *avoid* flashing a red border after the very first keystroke.
 */
export function isPartialIndianMobile(raw) {
  const n = normalizeIndianMobile(raw);
  if (n.length === 0) return true;
  if (n.length > 10) return false;
  return /^[6-9]/.test(n);
}

/**
 * One-line helper for the error message under the input. Returns the
 * empty string when the number is valid OR the user hasn't touched
 * the field yet (caller controls the `touched` gate).
 */
export function phoneErrorMessage(raw) {
  const n = normalizeIndianMobile(raw);
  if (n.length === 0) return 'Phone number is required.';
  if (!/^[6-9]/.test(n)) return 'Indian mobiles start with 6, 7, 8 or 9.';
  if (n.length < 10) return `${10 - n.length} more digit${10 - n.length === 1 ? '' : 's'} needed.`;
  if (n.length > 10) return 'Too many digits — Indian mobiles are 10 digits.';
  return '';
}
